import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

export interface AutoGrowTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Never shorter than this (px) — match the control it stands in for. */
  minHeight?: number;
  /** Grows to fit content up to this (px), then scrolls. */
  maxHeight?: number;
}

/**
 * Textarea whose height always equals its content, so a long value is read and
 * edited whole instead of through a one-line slot. Past `maxHeight` it stops
 * growing and scrolls — one very long value must not push the rest of a form
 * off screen.
 *
 * Deliberately uncontrolled with respect to height: the caller styles it like
 * any other input and never has to know how many lines the value takes.
 */
export const AutoGrowTextarea = forwardRef<
  HTMLTextAreaElement,
  AutoGrowTextareaProps
>(function AutoGrowTextarea(
  { className, minHeight = 32, maxHeight = 240, value, ...props },
  forwardedRef,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  // Layout effect, not effect: the height must be right BEFORE paint, otherwise
  // every keystroke that adds a line paints one frame at the old height first —
  // which reads as a flicker on exactly the interaction this component exists
  // to make smooth.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const content = el.scrollHeight;
    el.style.height = `${Math.min(Math.max(content, minHeight), maxHeight)}px`;
    el.style.overflowY = content > maxHeight ? "auto" : "hidden";
  }, [value, minHeight, maxHeight]);

  return (
    <textarea
      ref={setRefs}
      rows={1}
      value={value}
      className={cn("resize-none", className)}
      {...props}
    />
  );
});
